import html2canvas from 'html2canvas';

/** Opens every capture in a new tab as it is taken. Testing only — keep false when shipping. */
export const DEBUG_SCREENSHOT_NEW_TAB = false;

// browsers hand back a blank canvas past this, so the scale is clamped rather than the size
const MAX_CANVAS_PX = 16384;

// wrappers whose height clipping has to be released for the whole content to
// render. body is included because ngx-bootstrap sets overflow:hidden on it
// while a modal is open, and .login-main-wrapper is a mobile-breakpoint
// scroller — the kind of second, breakpoint-swapped scroller that is easy to
// miss and crops the capture on phones only.
const UNCLIP_SELECTOR = [
  'body',
  '.login-main-wrapper',
  '.legal-modal',
  '.modal-dialog',
  '.modal-body',
  '.vh-100',
  '[class*="overflow"]'
].join(', ');

export interface ConsentCaptureOptions {
  /** shows up in the console on failure, so multiple capture points stay distinguishable */
  label?: string;
  /**
   * Captures the visible viewport exactly as it looks — scroll positions kept,
   * nothing unclipped. Use it to record what someone was looking at when they
   * acted. The default instead releases every scroller to capture full content.
   */
  viewport?: boolean;
  /** selectors inside the target that clip height (ignored when viewport is set) */
  unclip?: string;
  mime?: 'image/png' | 'image/jpeg';
  quality?: number;
}

// marks live scrollers so the clone can be matched to them by attribute rather
// than by index, which conditionally rendered content easily knocks out of step
const SCROLL_ATTR = 'data-shot-scroll';

/**
 * html2canvas clones the DOM, and a cloned scroll container always starts at the
 * top — which is why a scrolled-to-the-bottom document comes back showing its
 * first page. Re-applying scrollTop on the clone is unreliable, so each scroller's
 * children are wrapped and shifted up by the offset instead.
 */
const markScrollPositions = (): HTMLElement[] => {
  const marked: HTMLElement[] = [];
  Array.from(document.querySelectorAll<HTMLElement>('*')).forEach(el => {
    if (el.scrollTop > 0) {
      el.setAttribute(SCROLL_ATTR, String(el.scrollTop));
      marked.push(el);
    }
  });
  return marked;
};

const applyScrollPositions = (clonedDoc: Document) => {
  Array.from(clonedDoc.querySelectorAll<HTMLElement>(`[${SCROLL_ATTR}]`)).forEach(el => {
    const offset = Number(el.getAttribute(SCROLL_ATTR)) || 0;
    if (!offset) return;
    const shifted = clonedDoc.createElement('div');
    while (el.firstChild) {
      shifted.appendChild(el.firstChild);
    }
    shifted.style.marginTop = `-${offset}px`;
    el.appendChild(shifted);
    el.style.overflow = 'hidden';
  });
};

/**
 * Height the target reaches once every scroller inside it is released.
 * Walking the tree for the deepest bottom edge (a common approach) measures
 * elements at their *current* positions, so anything sitting after a scroller —
 * a footer, in our case — gets cropped off. Adding back each scroller's hidden
 * overflow keeps whatever follows it inside the frame.
 */
const unclippedHeight = (root: HTMLElement): number => {
  let height = root.offsetHeight;
  Array.from(root.querySelectorAll<HTMLElement>('*')).forEach(el => {
    const hidden = el.scrollHeight - el.clientHeight;
    if (hidden <= 1) return;
    const overflowY = window.getComputedStyle(el).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') {
      height += hidden;
    }
  });
  return Math.ceil(height) + 2;
};

/**
 * Captures an element to a data URL, including content scrolled out of view.
 * Returns null rather than throwing — a failed capture must never block the
 * flow it is recording.
 */
export const captureConsentScreenshot = async (
  target: HTMLElement | null,
  options: ConsentCaptureOptions = {}
): Promise<string | null> => {
  const { label = 'consent', viewport = false, unclip = UNCLIP_SELECTOR, mime = 'image/jpeg', quality = 0.8 } = options;
  if (!target) return null;

  const marked = viewport ? markScrollPositions() : [];
  try {
    // webfonts render as fallbacks if the capture starts before they load
    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
    }

    // capturing the page itself frames the viewport; capturing an element frames
    // just that element, at the size it is currently rendered
    const isPageTarget = target === document.body || target === document.documentElement;

    // keep the real width — widening re-flows the layout and it stops looking
    // like the screen the user was actually on
    const width = viewport
      ? (isPageTarget ? document.documentElement.clientWidth : target.offsetWidth)
      : (target.offsetWidth || document.documentElement.clientWidth);
    const height = viewport
      ? (isPageTarget ? document.documentElement.clientHeight : target.offsetHeight)
      : unclippedHeight(target);
    const scale = Math.min(2, MAX_CANVAS_PX / Math.max(width, height));

    const options: any = {
      backgroundColor: '#ffffff',
      useCORS: true,
      scale,
      width,
      height,
      // the clone still has to lay out against the real viewport, or a fixed
      // element such as a modal lands somewhere else entirely
      windowWidth: viewport ? document.documentElement.clientWidth : width,
      windowHeight: viewport ? document.documentElement.clientHeight : height
    };
    // only the page target gets an explicit crop origin; for an element,
    // html2canvas works its position out itself and passing 0 would move the
    // crop to the top-left of the document instead
    if (viewport && isPageTarget) {
      options.x = window.scrollX;
      options.y = window.scrollY;
    }

    const canvas = await html2canvas(target, {
      ...options,
      onclone: (clonedDoc: Document) => {
        if (viewport) {
          applyScrollPositions(clonedDoc);
        } else {
          // released on the clone, so the live page never visibly jumps
          Array.from(clonedDoc.querySelectorAll<HTMLElement>(unclip)).forEach(el => {
            el.style.height = 'auto';
            el.style.minHeight = '0';
            el.style.maxHeight = 'none';
            el.style.overflow = 'visible';
          });
        }

        // html2canvas drops input text to the baseline
        const fixes = clonedDoc.createElement('style');
        fixes.textContent = 'input { line-height: normal !important; vertical-align: middle !important; }';
        clonedDoc.head.appendChild(fixes);
      }
    });

    return canvas.toDataURL(mime, quality);
  } catch (error) {
    console.error(`[consent] capture failed (${label})`, error);
    return null;
  } finally {
    marked.forEach(el => el.removeAttribute(SCROLL_ATTR));
  }
};

/**
 * Data URL -> Blob for upload. Shared base64ToBlob helpers commonly hardcode
 * image/jpeg, which uploads PNG bytes under a .jpg name, so the real MIME is
 * read back off the data URL.
 */
export const consentScreenshotToBlob = (dataUrl: string | null): Blob | null => {
  if (!dataUrl) return null;
  try {
    const [header, payload] = dataUrl.split(',');
    if (!payload) return null;
    const mime = /data:([^;]+)/.exec(header)?.[1] || 'image/png';
    const bytes = atob(payload);
    const buf = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) {
      buf[i] = bytes.charCodeAt(i);
    }
    return new Blob([buf], { type: mime });
  } catch (error) {
    console.error('[consent] could not convert screenshot to blob', error);
    return null;
  }
};

/** Debug helper: opens a capture in a new tab. */
export const openConsentScreenshotTab = (dataUrl: string | null) => {
  if (!dataUrl) return;
  try {
    const win = window.open('', '_blank');
    if (!win) return; // popup blocked
    win.document.write(
      '<!doctype html><title>Consent screenshot</title>' +
      '<body style="margin:0;background:#222;text-align:center">' +
      `<img src="${dataUrl}" style="max-width:100%"></body>`
    );
    win.document.close();
  } catch (error) {
    console.error('[consent] could not open screenshot tab', error);
  }
};
